import { useState } from "react";
import { useParams } from "react-router-dom";
import Layout from "../components/Layout";
import PageHeader from "../components/PageHeader";
import CallToAction from "../components/CallToAction";
import { Button } from "@/components/ui/button";
import { Check, ChevronDown, ChevronUp } from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Card, CardContent } from "@/components/ui/card";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from "@/components/ui/carousel";

// Mock data for services
const servicesData = {
  "korekcija-vek": {
    title: "Korekcija vek",
    description: "Pomladitev področja okoli oči za bolj svež in spočit videz.",
    longDescription: "Korekcija vek ali blefaroplastika je poseg, ki izboljša videz zgornjih in spodnjih vek z odstranitvijo odvečne kože, mišic in/ali maščobe. Poseg lahko dramatično pomladi področje okoli oči in vam da bolj spočit, mladosten videz.",
    bannerImage: "https://images.unsplash.com/photo-1571942794135-56e61c8ac80e?ixlib=rb-4.0.3&auto=format&fit=crop&w=2000&q=80",
    benefits: [
      "Odstranitev odvečne kože in zmanjšanje napihnjenosti",
      "Pomladitev področja okoli oči",
      "Odprt, bolj mladosten pogled",
      "Minimalne brazgotine in hitro okrevanje"
    ],
    procedure: [
      {
        step: 1,
        title: "Individualni posvet",
        description: "Podroben pregled in pogovor o vaših ciljih in pričakovanjih."
      },
      {
        step: 2,
        title: "Načrtovanje posega",
        description: "Natančen načrt posega, prilagojen vašim potrebam in anatomiji."
      },
      {
        step: 3,
        title: "Poseg",
        description: "Poseg običajno traja 1-2 uri in se izvaja v lokalni anesteziji ali sedaciji."
      },
      {
        step: 4,
        title: "Okrevanje",
        description: "Minimalno neugodje, otekline izginejo v 1-2 tednih, končni rezultati vidni po 4-6 tednih."
      }
    ],
    beforeAfterImages: [
      {
        before: "https://images.unsplash.com/photo-1531123897727-8f129e1688ce?ixlib=rb-4.0.3&auto=format&fit=crop&w=500&q=80",
        after: "https://images.unsplash.com/photo-1549351512-c5e12b11e283?ixlib=rb-4.0.3&auto=format&fit=crop&w=500&q=80"
      },
      {
        before: "https://images.unsplash.com/photo-1544005313-94ddf0286df2?ixlib=rb-4.0.3&auto=format&fit=crop&w=500&q=80",
        after: "https://images.unsplash.com/photo-1554151228-14d9def656e4?ixlib=rb-4.0.3&auto=format&fit=crop&w=500&q=80"
      }
    ],
    faq: [
      {
        question: "Koliko časa traja okrevanje po korekciji vek?",
        answer: "Večina pacientov se lahko vrne k običajnim aktivnostim po 7-10 dneh. Blažje otekline in modrice običajno izginejo v 1-2 tednih, končni rezultati pa so vidni po 4-6 tednih."
      },
      {
        question: "Ali bo poseg pustil vidne brazgotine?",
        answer: "Rezi so skrbno načrtovani v naravnih gubah in pregibih, zato so brazgotine po celjenju praktično neopazne."
      },
      {
        question: "Kako dolgo trajajo rezultati?",
        answer: "Rezultati korekcije vek so običajno dolgotrajni in lahko trajajo 5-10 let ali več, odvisno od genetike in življenjskega sloga."
      },
      {
        question: "Je poseg boleč?",
        answer: "Poseg se izvaja v lokalni anesteziji ali sedaciji, zato med samim posegom ne občutite bolečine. Po posegu je običajno prisotno le blago neugodje, ki ga uspešno obvladujemo s predpisanimi zdravili."
      }
    ]
  },
  "povecanje-prsi": {
    title: "Povečanje prsi",
    description: "Harmonično oblikovanje prsi z naprednimi tehnikami.",
    longDescription: "Povečanje prsi ali augmentacija je kirurški poseg, ki poveča velikost in izboljša obliko prsi z uporabo vsadkov. Poseg lahko dramatično izboljša samopodobo in občutek ženstvenosti.",
    bannerImage: "https://images.unsplash.com/photo-1511285605577-4d62fb50d2f7?ixlib=rb-4.0.3&auto=format&fit=crop&w=2000&q=80",
    benefits: [
      "Povečanje volumna in izboljšanje oblike prsi",
      "Obnova volumna po izgubi teže ali nosečnosti",
      "Izboljšanje simetrije in proporcionalnosti telesa",
      "Povečanje samozavesti in občutka ženstvenosti"
    ],
    procedure: [
      {
        step: 1,
        title: "Podroben posvet",
        description: "Pregled in pogovor o želeni velikosti, obliki in vrsti vsadkov."
      },
      {
        step: 2,
        title: "Izbira vsadkov",
        description: "Določitev optimalnega tipa, velikosti in oblike vsadkov za vaše telo."
      },
      {
        step: 3,
        title: "Operacija",
        description: "Poseg se izvaja v splošni anesteziji in traja 1-2 uri."
      },
      {
        step: 4,
        title: "Okrevanje",
        description: "Postopno vračanje k normalnim aktivnostim v 1-2 tednih, končni rezultati po 2-3 mesecih."
      }
    ],
    beforeAfterImages: [
      {
        before: "https://images.unsplash.com/photo-1569078449082-d264d9e239c5?ixlib=rb-4.0.3&auto=format&fit=crop&w=500&q=80",
        after: "https://images.unsplash.com/photo-1566616213894-2d4e1baee5d8?ixlib=rb-4.0.3&auto=format&fit=crop&w=500&q=80"
      },
      {
        before: "https://images.unsplash.com/photo-1587614203256-c0e9faec910e?ixlib=rb-4.0.3&auto=format&fit=crop&w=500&q=80",
        after: "https://images.unsplash.com/photo-1585184890038-2efd36bc4c7d?ixlib=rb-4.0.3&auto=format&fit=crop&w=500&q=80"
      }
    ],
    faq: [
      {
        question: "Kakšne vrste vsadkov so na voljo?",
        answer: "Na voljo so silikonski in fiziološki vsadki različnih oblik (okrogli ali anatomski) in velikosti. Dr. Novak bo z vami podrobno razpravljal o najboljši izbiri za vaše telo in želje."
      },
      {
        question: "Ali bodo vsadki videti in občutiti naravno?",
        answer: "Z modernimi vsadki in naprednimi kirurškimi tehnikami lahko dosežemo zelo naraven izgled in občutek prsi."
      },
      {
        question: "Kako dolgo trajajo vsadki?",
        answer: "Moderni vsadki so zelo trajni, vendar niso doživljenjski. Povprečna življenjska doba vsadkov je 10-15 let, vendar jih je v nekaterih primerih treba zamenjati prej."
      },
      {
        question: "Ali bom lahko dojila po posegu?",
        answer: "Večina žensk lahko normalno doji po povečanju prsi, saj se vsadki običajno namestijo pod mlečno žlezo ali mišico, kar ne vpliva na mlečne kanale."
      }
    ]
  }
};

const ServiceDetailPage = () => {
  const { slug } = useParams<{ slug: string }>();
  const [activeImageSet, setActiveImageSet] = useState(0);
  
  // Get service data based on slug
  const serviceData = slug && servicesData[slug as keyof typeof servicesData];
  
  if (!serviceData) {
    return (
      <Layout>
        <div className="container-section text-center">
          <h2 className="text-2xl font-bold mb-4">Storitev ni bila najdena</h2>
          <p className="text-gray-700">Žal storitev, ki jo iščete, ne obstaja.</p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      {/* Page Header with Background Image */}
      <div className="relative">
        <div className="absolute inset-0 bg-gradient-to-b from-black/50 to-transparent z-10"></div>
        <img 
          src={serviceData.bannerImage}
          alt={serviceData.title}
          className="w-full h-80 md:h-96 object-cover"
        />
        <div className="absolute inset-0 z-20 flex items-center">
          <div className="container mx-auto px-6 md:px-8">
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-4">
              {serviceData.title}
            </h1>
          </div>
        </div>
      </div>
      
      {/* Introduction Section */}
      <section className="bg-white py-16">
        <div className="container mx-auto px-6 md:px-8">
          <div className="max-w-3xl mx-auto">
            <p className="text-xl text-gray-700 leading-relaxed">
              {serviceData.longDescription}
            </p>
          </div>
        </div>
      </section>
      
      {/* Benefits Section */}
      <section className="bg-gray-50 py-16">
        <div className="container mx-auto px-6 md:px-8">
          <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-8 text-center">Prednosti</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-3xl mx-auto">
            {serviceData.benefits.map((benefit, index) => (
              <div key={index} className="flex items-start">
                <div className="bg-medical-100 p-2 rounded-full text-medical-600 mr-4 mt-1">
                  <Check size={18} />
                </div>
                <p className="text-gray-700 text-lg">{benefit}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
      
      {/* Procedure Steps Section */}
      <section className="bg-white py-16">
        <div className="container mx-auto px-6 md:px-8">
          <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-8 text-center">Potek posega</h2>
          
          <div className="max-w-4xl mx-auto">
            {serviceData.procedure.map((step) => (
              <div key={step.step} className="mb-8 last:mb-0">
                <div className="flex items-center mb-4">
                  <div className="w-12 h-12 rounded-full bg-medical-500 text-white flex items-center justify-center font-bold text-lg mr-4">
                    {step.step}
                  </div>
                  <h3 className="text-xl font-bold text-gray-900">{step.title}</h3>
                </div>
                <div className="pl-16">
                  <p className="text-gray-700">{step.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
      
      {/* Before/After Section */}
      <section className="bg-gray-50 py-16">
        <div className="container mx-auto px-6 md:px-8">
          <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-8 text-center">Pred / Po</h2>
          
          <div className="max-w-4xl mx-auto">
            <Carousel className="mx-auto">
              <CarouselContent>
                {serviceData.beforeAfterImages.map((imageSet, index) => (
                  <CarouselItem key={index}>
                    <Card>
                      <CardContent className="p-0">
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <p className="text-center font-medium text-gray-700 mb-2">Pred</p>
                            <img 
                              src={imageSet.before} 
                              alt="Pred posegom" 
                              className="w-full h-64 object-cover rounded-2xl"
                            />
                          </div>
                          <div>
                            <p className="text-center font-medium text-gray-700 mb-2">Po</p>
                            <img 
                              src={imageSet.after} 
                              alt="Po posegu" 
                              className="w-full h-64 object-cover rounded-2xl"
                            />
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </CarouselItem>
                ))}
              </CarouselContent>
              <CarouselPrevious />
              <CarouselNext />
            </Carousel>
            
            <div className="flex justify-center mt-4 gap-2">
              {serviceData.beforeAfterImages.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setActiveImageSet(index)}
                  className={`w-3 h-3 rounded-full ${
                    activeImageSet === index ? 'bg-medical-600' : 'bg-medical-200'
                  } transition-colors`}
                  aria-label={`Prikaži sliko ${index + 1}`}
                />
              ))}
            </div>
          </div>
        </div>
      </section>
      
      {/* FAQ Section */}
      <section className="bg-white py-16">
        <div className="container mx-auto px-6 md:px-8">
          <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-8 text-center">Pogosta vprašanja</h2>
          
          <div className="max-w-3xl mx-auto">
            <Accordion type="single" collapsible className="w-full">
              {serviceData.faq.map((item, index) => (
                <AccordionItem key={index} value={`item-${index}`} className="border-b border-gray-200">
                  <AccordionTrigger className="text-lg font-medium text-gray-900 py-4 hover:text-medical-600">
                    {item.question}
                  </AccordionTrigger>
                  <AccordionContent className="text-gray-700 pb-4">
                    {item.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </section>
      
      {/* CTA Section */}
      <CallToAction 
        title="Ste pripravljeni na svoj novi videz?"
        subtitle="Kontaktirajte nas za brezplačen posvet in začnite svoje potovanje do željenega videza."
      />
    </Layout>
  );
};

export default ServiceDetailPage;